// sharedSkillsApi — typed fetchers for /v1/platform/shared-skill-candidates
// and /v1/platform/shared-skills (Shared Skill Layer, spec
// docs/specs/shared-skill-layer.md §8). All endpoints are super_admin-gated
// by admin:shared_skills (backend enforces; frontend hides via
// RequireRole on the route).
import { apiFetch } from "../../lib/api-client.js";

// ─── Types ───────────────────────────────────────────────────────────────

export type SkillDomain =
  | "route_setup" | "task_setup" | "task_update"
  | "notification_rule" | "report_generation" | "incident_handling"
  | "onboarding" | "multi_site_rollout" | "roster_planning" | "general";

export type SkillRiskLevel = "low" | "medium" | "high" | "critical";

export type PrivacyRiskLevel = "none" | "low" | "medium" | "high" | "unscoreable";

export type CandidateStatus =
  | "proposed" | "reviewing" | "approved" | "rejected"
  | "request_changes" | "blocked" | "needs_manual_redaction";

export type ActiveSkillStatus = "active" | "deprecated" | "rejected";

export type ImpactType =
  | "retrieved_only" | "influenced_plan" | "blocked_action"
  | "required_confirmation" | "suggested_followup" | "changed_tool_parameters";

export type EvaluationOutcome =
  | "success" | "neutral" | "failed" | "rollback"
  | "user_rejected" | "admin_flagged";

export interface CandidateListItem {
  id: string;
  public_id: string | null;
  status: CandidateStatus;
  proposed_title: string;
  proposed_summary: string;
  proposed_domain: SkillDomain;
  proposed_risk_level: SkillRiskLevel;
  privacy_risk_level: PrivacyRiskLevel;
  quality_score: number;
  created_by_type: "ai" | "admin";
  created_by_user_id: string | null;
  reviewed_by_user_id: string | null;
  reviewed_at: string | null;
  approved_skill_id: string | null;
  source_thread_count: number;
  source_event_count: number;
  created_at: string;
  updated_at: string;
}

export interface CandidateDetail extends CandidateListItem {
  proposed_skill_json: string;
  source_events_json: string | null;          // null when caller lacks :debug scope
  source_tenant_ids_hash_json: string | null;
  source_context_summary: string | null;
  anonymization_result_json: string;
  review_notes: string | null;
  dedup_hash: string;
  parent_candidate_id: string | null;
  recent_reviews: ReviewRow[];
}

export interface ReviewRow {
  id: string;
  reviewer_user_id: string;
  action: string;
  review_notes: string | null;
  created_at: string;
}

export interface ActiveSkillListItem {
  id: string;
  public_id: string;
  version: number;
  status: ActiveSkillStatus;
  superseded_by: string | null;
  title: string;
  summary: string;
  domain: SkillDomain;
  risk_level: SkillRiskLevel;
  priority: number;
  source_type: "ai_proposed" | "admin_authored" | "admin_promoted";
  approved_by_user_id: string;
  approved_at: string;
  last_evaluated_at: string | null;
  created_at: string;
  updated_at: string;
  deprecated_at: string | null;
}

export interface ActiveSkillDetail extends ActiveSkillListItem {
  applicable_scenarios: string;
  trigger_conditions: string;
  recommended_behavior: string;
  forbidden_behavior: string;
  required_checks: string;
  related_tools: string;
  examples_json: string;
  source_candidate_id: string | null;
  tenant_data_removed: number;
  anonymization_notes: string | null;
  metadata_json: string | null;
  success_metrics_json: string | null;
  failure_metrics_json: string | null;
}

export interface UsageLogRow {
  id: string;
  tenant_id: string | null;
  user_id: string;
  conversation_id: string;
  message_id: string | null;
  operation_id: string | null;
  skill_id: string;
  skill_version: number;
  matched_reason: string;
  match_score: number | null;
  applied_context: string | null;
  impact_type: ImpactType;
  correlation_id: string;
  created_at: string;
}

export interface EvaluationRow {
  id: string;
  skill_id: string;
  skill_version: number;
  usage_log_id: string | null;
  tenant_id_hash: string | null;
  operation_id: string | null;
  outcome: EvaluationOutcome;
  user_confirmed: number | null;
  user_rejected: number | null;
  rollback_triggered: number | null;
  error_occurred: number | null;
  admin_flagged: number | null;
  notes: string | null;
  created_at: string;
}

export interface MetricsResponse {
  skill_id: string;
  window: "7d" | "30d" | "all";
  usage: {
    total: number;
    by_impact_type: Record<string, number>;
    distinct_tenants: number;
  };
  evaluations: {
    total: number;
    by_outcome: Record<string, number>;
  };
}

// ─── Error class ─────────────────────────────────────────────────────────

export class SharedSkillsApiError extends Error {
  status: number; code: string; correlationId: string | null; detail?: string;
  constructor(status: number, code: string, correlationId: string | null, detail?: string) {
    super(code); this.status = status; this.code = code; this.correlationId = correlationId; this.detail = detail;
  }
}

export function friendlySharedSkillsError(err: unknown): string {
  if (err instanceof SharedSkillsApiError) {
    if (err.status === 401) return "登入狀態已逾時，請重新登入後再試。";
    if (err.status === 403) return "您目前的角色不能存取共用技能管理（需要 super_admin）。";
    if (err.status === 404) return "找不到此資源。";
    if (err.code === "invalid_transition") return "目前狀態不允許此動作。";
    if (err.code === "duplicate_active_candidate") return "已有相同內容的候選正在審核中，無法重複建立。";
    if (err.code === "not_editable") return "此版本已停用或被取代，無法直接編輯（請建立新版本）。";
    if (err.code === "active_version_exists") return "已有另一個 active 版本存在，無法重啟此版。";
    if (err.code === "superseded_cannot_reactivate") return "此版已被新版本取代，無法重啟（請改用最新版）。";
    if (err.code === "public_id_version_collision") return "對應 public_id 已存在相同 version；請改 supersede 或變更 public_id。";
    if (err.code === "reason_required") return "請填寫原因。";
    if (err.code === "notes_required") return "請填寫修改說明。";
    if (err.code === "mode_target_mismatch") return "redact mode 與目前狀態不符。";
    if (err.code === "no_fields_to_update") return "沒有要更新的欄位。";
    return `操作失敗（HTTP ${err.status}・code=${err.code}・cid=${err.correlationId ?? "n/a"}）。`;
  }
  return "連線中斷，請檢查網路後再試。";
}

async function asError(r: Response): Promise<SharedSkillsApiError> {
  let code = "request_failed";
  let cid: string | null = r.headers.get("x-correlation-id");
  let detail: string | undefined;
  try {
    const body = (await r.json()) as { error?: string; correlation_id?: string; message?: string; detail?: string };
    code = body.error ?? code;
    cid = body.correlation_id ?? cid;
    detail = body.message ?? body.detail;
  } catch { /* tolerate non-JSON body */ }
  return new SharedSkillsApiError(r.status, code, cid, detail);
}

// ─── Candidate endpoints ─────────────────────────────────────────────────

export interface CandidatesListQuery {
  status?: CandidateStatus[];
  domain?: SkillDomain;
  risk_level?: SkillRiskLevel;
  privacy_risk_level?: PrivacyRiskLevel;
  created_by_type?: "ai" | "admin";
  q?: string;
  limit?: number;
  offset?: number;
}

function buildQs(q: Record<string, string | number | string[] | undefined>): string {
  const usp = new URLSearchParams();
  for (const [k, v] of Object.entries(q)) {
    if (v === undefined || v === null) continue;
    if (Array.isArray(v)) {
      if (v.length === 0) continue;
      usp.set(k, v.join(","));
    } else {
      usp.set(k, String(v));
    }
  }
  const s = usp.toString();
  return s ? `?${s}` : "";
}

export async function listCandidates(p: CandidatesListQuery = {}): Promise<{ items: CandidateListItem[]; total: number }> {
  const r = await apiFetch(`/v1/platform/shared-skill-candidates${buildQs(p as never)}`, { credentials: "include" });
  if (!r.ok) throw await asError(r);
  return (await r.json()) as { items: CandidateListItem[]; total: number };
}

export async function getCandidate(id: string): Promise<CandidateDetail> {
  const r = await apiFetch(`/v1/platform/shared-skill-candidates/${encodeURIComponent(id)}`, { credentials: "include" });
  if (!r.ok) throw await asError(r);
  return (await r.json()) as CandidateDetail;
}

export interface CreateCandidateBody {
  proposed_title: string;
  proposed_summary: string;
  proposed_domain: SkillDomain;
  proposed_risk_level: SkillRiskLevel;
  proposed_skill_json: unknown;
  source_context_summary?: string | null;
  privacy_risk_level?: PrivacyRiskLevel;
  quality_score?: number;
}

export async function createCandidate(body: CreateCandidateBody): Promise<{ id: string; status: CandidateStatus }> {
  const r = await apiFetch(`/v1/platform/shared-skill-candidates`, {
    method: "POST", credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!r.ok) throw await asError(r);
  return (await r.json()) as { id: string; status: CandidateStatus };
}

export type PatchCandidateBody = Partial<CreateCandidateBody> & { review_notes?: string | null };

export async function patchCandidate(id: string, body: PatchCandidateBody): Promise<{ id: string }> {
  const r = await apiFetch(`/v1/platform/shared-skill-candidates/${encodeURIComponent(id)}`, {
    method: "PATCH", credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!r.ok) throw await asError(r);
  return (await r.json()) as { id: string };
}

export interface ApproveCandidateBody {
  title?: string;
  summary?: string;
  domain?: SkillDomain;
  applicable_scenarios?: string;
  trigger_conditions?: string;
  recommended_behavior?: string;
  forbidden_behavior?: string;
  required_checks?: string[];
  related_tools?: string[];
  risk_level?: SkillRiskLevel;
  examples?: unknown[];
  priority?: number;
  public_id?: string;
  supersedes_public_id?: string;
  review_notes?: string;
}

export async function approveCandidate(id: string, body: ApproveCandidateBody = {}): Promise<{ skill_id: string; public_id: string; version: number }> {
  const r = await apiFetch(`/v1/platform/shared-skill-candidates/${encodeURIComponent(id)}/approve`, {
    method: "POST", credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!r.ok) throw await asError(r);
  return (await r.json()) as { skill_id: string; public_id: string; version: number };
}

export async function rejectCandidate(id: string, reason: string, reviewNotes?: string): Promise<void> {
  const r = await apiFetch(`/v1/platform/shared-skill-candidates/${encodeURIComponent(id)}/reject`, {
    method: "POST", credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ reason, review_notes: reviewNotes }),
  });
  if (!r.ok) throw await asError(r);
}

export async function requestChangesCandidate(id: string, notes: string): Promise<void> {
  const r = await apiFetch(`/v1/platform/shared-skill-candidates/${encodeURIComponent(id)}/request-changes`, {
    method: "POST", credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ notes }),
  });
  if (!r.ok) throw await asError(r);
}

export async function redactCandidate(id: string, body: { mode: "escalate" | "complete"; overrides?: PatchCandidateBody & { privacy_risk_level?: PrivacyRiskLevel; anonymization_notes?: string } }): Promise<void> {
  const r = await apiFetch(`/v1/platform/shared-skill-candidates/${encodeURIComponent(id)}/redact`, {
    method: "POST", credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!r.ok) throw await asError(r);
}

// ─── Active skill endpoints ──────────────────────────────────────────────

export interface SkillsListQuery {
  status?: ActiveSkillStatus[];
  domain?: SkillDomain;
  risk_level?: SkillRiskLevel;
  q?: string;
  include_superseded?: boolean;
  limit?: number;
  offset?: number;
}

export async function listActiveSkills(p: SkillsListQuery = {}): Promise<{ items: ActiveSkillListItem[]; total: number }> {
  const q: Record<string, string | number | string[] | undefined> = {
    status: p.status,
    domain: p.domain,
    risk_level: p.risk_level,
    q: p.q,
    limit: p.limit,
    offset: p.offset,
  };
  if (typeof p.include_superseded === "boolean") {
    q.include_superseded = p.include_superseded ? "1" : "0";
  }
  const r = await apiFetch(`/v1/platform/shared-skills${buildQs(q)}`, { credentials: "include" });
  if (!r.ok) throw await asError(r);
  return (await r.json()) as { items: ActiveSkillListItem[]; total: number };
}

export async function getActiveSkill(id: string): Promise<ActiveSkillDetail> {
  const r = await apiFetch(`/v1/platform/shared-skills/${encodeURIComponent(id)}`, { credentials: "include" });
  if (!r.ok) throw await asError(r);
  return (await r.json()) as ActiveSkillDetail;
}

export async function getSkillHistory(publicId: string): Promise<{ items: ActiveSkillListItem[] }> {
  const r = await apiFetch(`/v1/platform/shared-skills/by-public-id/${encodeURIComponent(publicId)}/history`, { credentials: "include" });
  if (!r.ok) throw await asError(r);
  return (await r.json()) as { items: ActiveSkillListItem[] };
}

export interface PatchActiveSkillBody {
  title?: string;
  summary?: string;
  domain?: SkillDomain;
  applicable_scenarios?: string;
  trigger_conditions?: string;
  recommended_behavior?: string;
  forbidden_behavior?: string;
  required_checks?: string[];
  related_tools?: string[];
  risk_level?: SkillRiskLevel;
  examples?: unknown[];
  priority?: number;
  anonymization_notes?: string | null;
  metadata_json?: string | null | Record<string, unknown>;
}

export async function patchActiveSkill(id: string, body: PatchActiveSkillBody): Promise<{ id: string; version: number; previous_id: string }> {
  const r = await apiFetch(`/v1/platform/shared-skills/${encodeURIComponent(id)}`, {
    method: "PATCH", credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!r.ok) throw await asError(r);
  return (await r.json()) as { id: string; version: number; previous_id: string };
}

export async function deprecateSkill(id: string, reason: string): Promise<void> {
  const r = await apiFetch(`/v1/platform/shared-skills/${encodeURIComponent(id)}/deprecate`, {
    method: "POST", credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ reason }),
  });
  if (!r.ok) throw await asError(r);
}

export async function reactivateSkill(id: string, reason: string): Promise<void> {
  const r = await apiFetch(`/v1/platform/shared-skills/${encodeURIComponent(id)}/reactivate`, {
    method: "POST", credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ reason }),
  });
  if (!r.ok) throw await asError(r);
}

export async function getSkillUsage(id: string, opts: { impact_type?: ImpactType; since?: string; limit?: number; offset?: number } = {}): Promise<{ items: UsageLogRow[]; total: number }> {
  const r = await apiFetch(`/v1/platform/shared-skills/${encodeURIComponent(id)}/usage${buildQs(opts as never)}`, { credentials: "include" });
  if (!r.ok) throw await asError(r);
  return (await r.json()) as { items: UsageLogRow[]; total: number };
}

export async function getSkillEvaluations(id: string, opts: { outcome?: EvaluationOutcome; since?: string; limit?: number; offset?: number } = {}): Promise<{ items: EvaluationRow[]; total: number }> {
  const r = await apiFetch(`/v1/platform/shared-skills/${encodeURIComponent(id)}/evaluations${buildQs(opts as never)}`, { credentials: "include" });
  if (!r.ok) throw await asError(r);
  return (await r.json()) as { items: EvaluationRow[]; total: number };
}

export async function getSkillMetrics(id: string, window: "7d" | "30d" | "all" = "30d"): Promise<MetricsResponse> {
  const r = await apiFetch(`/v1/platform/shared-skills/${encodeURIComponent(id)}/metrics?window=${window}`, { credentials: "include" });
  if (!r.ok) throw await asError(r);
  return (await r.json()) as MetricsResponse;
}

// ─── zh-TW label helpers ─────────────────────────────────────────────────

export const DOMAIN_LABEL: Record<SkillDomain, string> = {
  route_setup: "巡邏路線設定",
  task_setup: "任務建立",
  task_update: "任務更新",
  notification_rule: "通知規則",
  report_generation: "報表產出",
  incident_handling: "異常處理",
  onboarding: "客戶導入",
  multi_site_rollout: "多案場 rollout",
  roster_planning: "排班規劃",
  general: "一般通用",
};

export const RISK_LABEL: Record<SkillRiskLevel, string> = {
  low: "低", medium: "中", high: "高", critical: "極高",
};

export const PRIVACY_LABEL: Record<PrivacyRiskLevel, string> = {
  none: "無", low: "低", medium: "中", high: "高", unscoreable: "無法評估",
};

export const CANDIDATE_STATUS_LABEL: Record<CandidateStatus, string> = {
  proposed: "已提案",
  reviewing: "審核中",
  approved: "已批准",
  rejected: "已拒絕",
  request_changes: "要求修改",
  blocked: "已封鎖",
  needs_manual_redaction: "需手動去識別化",
};

export const ACTIVE_STATUS_LABEL: Record<ActiveSkillStatus, string> = {
  active: "啟用中",
  deprecated: "已停用",
  rejected: "已拒絕",
};

export const IMPACT_LABEL: Record<ImpactType, string> = {
  retrieved_only: "僅檢索",
  influenced_plan: "影響 plan",
  blocked_action: "阻擋動作",
  required_confirmation: "要求二次確認",
  suggested_followup: "建議後續",
  changed_tool_parameters: "改變工具參數",
};

export const OUTCOME_LABEL: Record<EvaluationOutcome, string> = {
  success: "成功",
  neutral: "中性",
  failed: "失敗",
  rollback: "回滾",
  user_rejected: "使用者拒絕",
  admin_flagged: "管理員標記",
};
